/**
 * Widget UI strings — the ~6 chrome strings that need translation.
 * Agent content (greeting, suggestions, responses) is configured separately.
 *
 * Resolution: explicit `strings` override > `locale` bundle > English default
 */

export interface ChatStrings {
  placeholder: string;       // Input placeholder (supports {agent} token)
  poweredBy: string;         // "Powered by"
  privacyText: string;       // "By chatting, you agree to our"
  privacyLink: string;       // "privacy policy"
  newChat: string;           // Menu: "New chat"
  closeChat: string;         // Aria: "Close chat"
  openChat: string;          // Aria: "Open chat"
  typing: string;            // Screen reader: "Agent is typing"
  // Conversation controls (optional — English fallback at call site when a
  // locale bundle omits them, so new strings never block translation).
  restartTitle?: string;         // Confirm dialog: "Restart conversation?"
  restartDescription?: string;   // Confirm dialog body
  restartConfirm?: string;       // Confirm button: "Restart"
  cancel?: string;               // Cancel button
  previousConversations?: string; // Menu section header
  noConversations?: string;       // Empty state in the menu
  handoff?: string;               // "A team member will reply here shortly."
}

const LOCALES: Record<string, ChatStrings> = {
  en: {
    placeholder: 'Message {agent}...',
    poweredBy: 'Powered by',
    privacyText: 'By chatting, you agree to our',
    privacyLink: 'privacy policy',
    newChat: 'New chat',
    closeChat: 'Close chat',
    openChat: 'Open chat',
    typing: 'Agent is typing',
    restartTitle: 'Restart conversation?',
    restartDescription: 'This starts a fresh chat. You can still reopen this one from the menu.',
    restartConfirm: 'Restart',
    cancel: 'Cancel',
    previousConversations: 'Previous conversations',
    noConversations: 'No previous conversations',
    handoff: 'A team member will reply to you here shortly.',
  },
  es: {
    placeholder: 'Escribe a {agent}...',
    poweredBy: 'Desarrollado por',
    privacyText: 'Al chatear, aceptas nuestra',
    privacyLink: 'política de privacidad',
    newChat: 'Nueva conversación',
    closeChat: 'Cerrar chat',
    openChat: 'Abrir chat',
    typing: 'El agente está escribiendo',
  },
  pt: {
    placeholder: 'Mensagem para {agent}...',
    poweredBy: 'Desenvolvido por',
    privacyText: 'Ao conversar, você concorda com nossa',
    privacyLink: 'política de privacidade',
    newChat: 'Nova conversa',
    closeChat: 'Fechar chat',
    openChat: 'Abrir chat',
    typing: 'O agente está digitando',
  },
  fr: {
    placeholder: 'Écrire à {agent}...',
    poweredBy: 'Propulsé par',
    privacyText: 'En discutant, vous acceptez notre',
    privacyLink: 'politique de confidentialité',
    newChat: 'Nouvelle conversation',
    closeChat: 'Fermer le chat',
    openChat: 'Ouvrir le chat',
    typing: "L'agent est en train d'écrire",
  },
  de: {
    placeholder: 'Nachricht an {agent}...',
    poweredBy: 'Bereitgestellt von',
    privacyText: 'Durch das Chatten stimmen Sie unserer',
    privacyLink: 'Datenschutzrichtlinie',
    newChat: 'Neues Gespräch',
    closeChat: 'Chat schließen',
    openChat: 'Chat öffnen',
    typing: 'Der Agent schreibt',
  },
  ja: {
    placeholder: '{agent}にメッセージ...',
    poweredBy: 'Powered by',
    privacyText: 'チャットすることで、当社の',
    privacyLink: 'プライバシーポリシー',
    newChat: '新しいチャット',
    closeChat: 'チャットを閉じる',
    openChat: 'チャットを開く',
    typing: 'エージェントが入力中',
  },
  zh: {
    placeholder: '给{agent}发消息...',
    poweredBy: '技术支持',
    privacyText: '继续聊天即表示您同意我们的',
    privacyLink: '隐私政策',
    newChat: '新对话',
    closeChat: '关闭聊天',
    openChat: '打开聊天',
    typing: '客服正在输入',
  },
  ar: {
    placeholder: 'أرسل رسالة إلى {agent}...',
    poweredBy: 'مدعوم من',
    privacyText: 'بالمحادثة، أنت توافق على',
    privacyLink: 'سياسة الخصوصية',
    newChat: 'محادثة جديدة',
    closeChat: 'إغلاق المحادثة',
    openChat: 'فتح المحادثة',
    typing: 'الوكيل يكتب',
  },
};

/**
 * Resolve strings: explicit overrides > locale bundle > English
 */
export function resolveStrings(
  locale?: string,
  overrides?: Partial<ChatStrings>,
): ChatStrings {
  const base = LOCALES[locale ?? 'en'] ?? LOCALES.en;
  if (!overrides) return base;
  return { ...base, ...overrides };
}

/**
 * Replace {agent} token in a string
 */
export function t(str: string, agentName?: string): string {
  return str.replace(/\{agent\}/g, agentName ?? 'AI');
}

export { LOCALES };
